import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check, KeyRound, Copy, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/bits";

const EMPTY = {
  name: "", audience: "farmer", state: "", language: "", dialect: "",
  summary: "", voice: "aarav", model: "gemini-3.1-flash-lite", crops: "", prompt: "",
};

export default function Platform() {
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const [users, catalog] = await Promise.all([
      api.get("/platform/users"),
      api.get("/platform/templates"),
    ]);
    setAccounts(users.data.members || []);
    setTemplates(catalog.data.templates || []);
  };

  useEffect(() => { load().catch((e) => toast.error(e.response?.data?.detail || "Could not load platform data")); }, []);

  const activate = async (userId) => {
    setBusy(true);
    try {
      await api.post(`/platform/users/${userId}/activate`);
      toast.success("Account activated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  const setTempPassword = async (member) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/users/${member.user_id}/temp-password`);
      setIssued({ email: member.email, name: member.name, password: data.temporary_password });
      setCopied(false);
      toast.success("Temporary password created — send it out of band");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set a temporary password");
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    if (!issued?.password) return;
    try {
      await navigator.clipboard.writeText(issued.password);
      setCopied(true);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — select the password instead");
    }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/templates", {
        ...form,
        tools: ["po", "inventory", "nearest_retailer", "weather"],
        kb_sources: ["catalogue", "agri_dataset", "retailer_network", "weather_satellite"],
      });
      toast.success("Template saved");
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save template");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/platform/templates/${id}`);
      toast.success("Template removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete template");
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pending = accounts.filter((m) => m.status === "pending");

  return (
    <div>
      <PageHeader overline="Platform" title="Activation & templates" desc="Activate accounts, set a temporary password out of band, and manage the global agent template catalog." testId="platform-header" />
      {issued && (
        <div className="glass p-5 mb-5 border border-amber-500/30 bg-amber-950/20" data-testid="platform-temp-password">
          <p className="overline-label text-amber-300">Temporary password — shown once</p>
          <p className="text-sm text-slate-200 mt-1">Send this to {issued.name || issued.email} by call, WhatsApp, or Slack. They sign in and must change it.</p>
          <p className="text-[11px] text-slate-500 mt-1">{issued.email}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="font-mono text-base text-amber-100 bg-black/40 px-3 py-2 rounded-lg" data-testid="platform-temp-password-value">{issued.password}</code>
            <button type="button" onClick={copyPassword} className="btn-ghost btn-sm" data-testid="platform-temp-password-copy">
              {copied ? <CheckCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
            </button>
            <button type="button" onClick={() => setIssued(null)} className="text-[11px] font-mono text-slate-500 hover:text-slate-200">Dismiss</button>
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 space-y-5">
          <div className="glass overflow-hidden rise" data-testid="platform-pending">
            <p className="overline-label px-5 pt-5 mb-2">Pending accounts</p>
            {pending.map((m) => (
              <div key={m.user_id} className="flex items-center gap-3 px-5 py-3 border-t border-white/5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-100 truncate">{m.name || m.email}</p>
                  <p className="text-[11px] text-slate-500 truncate">{m.email} · {m.role}</p>
                </div>
                <button disabled={busy} onClick={() => activate(m.user_id)} className="btn-primary btn-sm" data-testid={`platform-activate-${m.user_id}`}>
                  <Check className="w-3.5 h-3.5" /> Activate
                </button>
              </div>
            ))}
            {!pending.length && <p className="text-xs text-slate-500 px-5 pb-5">No pending accounts.</p>}
          </div>
          <div className="glass overflow-hidden rise" data-testid="platform-accounts">
            <p className="overline-label px-5 pt-5 mb-2">All accounts</p>
            {accounts.map((m) => (
              <div key={m.user_id} className="flex items-center gap-3 px-5 py-3 border-t border-white/5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-100 truncate">{m.name || m.email}</p>
                  <p className="text-[11px] text-slate-500 truncate">{m.email} · {m.status}{m.must_change_password ? " · must change password" : ""}</p>
                </div>
                {m.email_verified ? (
                  <button disabled={busy} onClick={() => setTempPassword(m)} className="btn-ghost btn-sm" data-testid={`platform-temp-password-${m.user_id}`}>
                    <KeyRound className="w-3.5 h-3.5" /> Set temp password
                  </button>
                ) : (
                  <span className="text-[11px] text-slate-600">Unverified</span>
                )}
              </div>
            ))}
            {!accounts.length && <p className="text-xs text-slate-500 px-5 pb-5">No accounts yet.</p>}
          </div>
        </div>
        <div className="lg:col-span-7 space-y-5">
          <form onSubmit={saveTemplate} className="glass p-5 rise space-y-3" data-testid="platform-template-form">
            <p className="overline-label">Add template</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="field" placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="platform-tpl-name" />
              <select className="field" value={form.audience} onChange={(e) => set("audience", e.target.value)} data-testid="platform-tpl-audience">
                <option value="farmer">Farmer</option>
                <option value="retailer">Retailer</option>
              </select>
              <input className="field" placeholder="State" value={form.state} onChange={(e) => set("state", e.target.value)} />
              <input className="field" placeholder="Language" value={form.language} onChange={(e) => set("language", e.target.value)} />
              <input className="field" placeholder="Dialect" value={form.dialect} onChange={(e) => set("dialect", e.target.value)} />
              <input className="field" placeholder="Crops" value={form.crops} onChange={(e) => set("crops", e.target.value)} />
            </div>
            <input className="field" placeholder="Summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} />
            <textarea className="field min-h-[90px]" placeholder="Prompt" value={form.prompt} onChange={(e) => set("prompt", e.target.value)} />
            <button disabled={busy} className="btn-primary" data-testid="platform-tpl-save"><Plus className="w-4 h-4" /> Save template</button>
          </form>
          <div className="glass overflow-hidden rise" data-testid="platform-templates">
            <p className="overline-label px-5 pt-5 mb-2">Catalog</p>
            {templates.map((t) => (
              <div key={t.id} className="flex items-start gap-3 px-5 py-3 border-t border-white/5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-100">{t.name}</p>
                  <p className="text-[11px] text-slate-500">{t.audience} · {t.state} · {t.language}</p>
                </div>
                <button disabled={busy} onClick={() => remove(t.id)} className="text-slate-500 hover:text-rose-300 p-2" data-testid={`platform-tpl-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
